import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { AlertCircle, ChevronRight, Home, Grid3x3, Eye, Plus, Loader, Save } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import cuttingPlanService from '../services/cuttingPlanService';
import projectsService from '../services/projectsService';
import toast from 'react-hot-toast';
import CuttingPlanHeader from './CuttingPlanHeader';
import CuttingPlanSummary from './CuttingPlanSummary';
import CuttingPlanPartsList from './CuttingPlanPartsList';
import CuttingPlanBoardsPreview from './CuttingPlanBoardsPreview';
import CuttingPlanTimeline from './CuttingPlanTimeline';
import BoardCard from './BoardCard';
import BoardFormModal from './BoardFormModal';
import ConfirmModal from './ConfirmModal';

const CuttingPlanDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();

  const [plan, setPlan] = useState(null);
  const [parts, setParts] = useState([]);
  const [boards, setBoards] = useState([]);
  const [positions, setPositions] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeTab, setActiveTab] = useState('view');
  const [showBoardForm, setShowBoardForm] = useState(false);
  const [editingBoard, setEditingBoard] = useState(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmCallback, setConfirmCallback] = useState(null);

  const canAccess = hasRole?.(['GERENCIA']) || hasRole?.(['RECEPCAO_COMPRAS_AGENDA']);
  const isNewPlan = id === 'new';

  const [newPlanForm, setNewPlanForm] = useState({
    projectId: '',
    boardWidth: 2.75,
    boardHeight: 1.85,
    kerfWidth: 0.0025,
    notes: ''
  });
  const [isCreating, setIsCreating] = useState(false);

  const { data: projectsData } = useQuery({
    queryKey: ['projects-for-cut-plan'],
    queryFn: () => projectsService.getAllProjects({ limit: 100 }),
    enabled: isNewPlan
  });

  const projects = projectsData?.data?.data || projectsData?.data?.projects || [];

  const handleConfirm = () => {
    if (confirmCallback) {
      confirmCallback();
    }
    setShowConfirmModal(false);
    setConfirmCallback(null);
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
    setConfirmCallback(null);
  };

  const handleCreatePlan = async (e) => {
    e.preventDefault();
    setIsCreating(true);
    try {
      const result = await cuttingPlanService.createPlan(newPlanForm);
      if (result.success) {
        toast.success('Plano de corte criado!');
        navigate(`/cutting-plans/${result.data.id}`);
      } else {
        toast.error(result.message || 'Erro ao criar plano');
      }
    } catch (error) {
      toast.error('Erro ao criar plano de corte');
    } finally {
      setIsCreating(false);
    }
  };

  const loadPlanData = async () => {
    if (!canAccess) {
      setError('Você não tem permissão para acessar este recurso');
      setLoading(false);
      return;
    }

    if (isNewPlan) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const planResponse = await cuttingPlanService.getPlanById(id);
      if (planResponse.success && planResponse.data) {
        setPlan(planResponse.data);
      } else {
        setError('Plano de corte não encontrado');
        setLoading(false);
        return;
      }

      const [boardsData, positionsData] = await Promise.all([
        cuttingPlanService.getBoardsByPlan(id),
        cuttingPlanService.getPositionsByPlan(id)
      ]);

      if (boardsData.success && boardsData.data) {
        setBoards(boardsData.data);
      }
      if (positionsData.success && positionsData.data) {
        setPositions(positionsData.data);
      }
    } catch (error) {
      console.error('Erro ao carregar dados do plano:', error);
      setError('Erro ao carregar dados do plano de corte');
      toast.error('Erro ao carregar dados do plano');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlanData();
  }, [id, canAccess]);

  const handleEdit = (plan) => {
    navigate(`/cutting-plans/${id}?edit=true`);
  };

  const handleDelete = async (plan) => {
    setConfirmMessage(`Tem certeza que deseja excluir o plano ${plan.plan_code}?`);
    setConfirmCallback(() => async () => {
      try {
        await cuttingPlanService.deletePlan(plan.id);
        toast.success('Plano excluído com sucesso');
        navigate('/cutting-plans');
      } catch (error) {
        console.error('Erro ao deletar plano:', error);
        toast.error('Erro ao deletar plano de corte');
      }
    });
    setShowConfirmModal(true);
  };

  const handleCreateBoard = () => {
    setEditingBoard(null);
    setShowBoardForm(true);
  };

  const handleEditBoard = (board) => {
    setEditingBoard(board);
    setShowBoardForm(true);
  };

  const handleDeleteBoard = async (board) => {
    setConfirmMessage(`Tem certeza que deseja excluir a chapa ${board.board_number}?`);
    setConfirmCallback(() => async () => {
      try {
        await cuttingPlanService.deleteBoard(board.id);
        toast.success('Chapa excluída com sucesso');
        loadPlanData();
      } catch (error) {
        console.error('Erro ao deletar chapa:', error);
        toast.error('Erro ao deletar chapa');
      }
    });
    setShowConfirmModal(true);
  };

  const handleSaveBoard = async (boardId, data) => {
    try {
      if (boardId) {
        await cuttingPlanService.updateBoard(boardId, data);
        toast.success('Chapa atualizada com sucesso');
      } else {
        await cuttingPlanService.createBoard({ ...data, cutPlanId: id });
        toast.success('Chapa criada com sucesso');
      }
      loadPlanData();
    } catch (error) {
      console.error('Erro ao salvar chapa:', error);
      toast.error(error.message || 'Erro ao salvar chapa');
      throw error;
    }
  };

  const handleViewModules = () => {
    navigate(`/cutting-plans/${id}/modules`);
  };

  const handleViewBoardCut = (board) => {
    navigate(`/cutting-plans/${id}/boards/${board.id}`);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-24 bg-gray-200 rounded-lg"></div>
          <div className="h-48 bg-gray-200 rounded-lg"></div>
          <div className="h-64 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-center gap-3">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
          <div>
            <p className="text-red-800 font-medium">{error}</p>
            <button
              onClick={() => navigate('/cutting-plans')}
              className="text-red-600 hover:text-red-800 text-sm mt-1"
            >
              Voltar para lista de planos
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isNewPlan) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <button
            onClick={() => navigate('/cutting-plans')}
            className="text-blue-600 hover:text-blue-800 text-sm inline-flex items-center gap-1"
          >
            <ChevronRight className="w-4 h-4 rotate-180" />
            Voltar para lista de planos
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Criar Novo Plano de Corte</h2>

          <form onSubmit={handleCreatePlan} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Projeto (opcional)
              </label>
              <select
                value={newPlanForm.projectId}
                onChange={(e) => setNewPlanForm({ ...newPlanForm, projectId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Selecione um projeto (opcional)</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.project_code} - {project.client_name || project.client_id} - {project.address || 'Sem endereço'}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Largura da Chapa (m)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={newPlanForm.boardWidth}
                  onChange={(e) => setNewPlanForm({ ...newPlanForm, boardWidth: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Altura da Chapa (m)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={newPlanForm.boardHeight}
                  onChange={(e) => setNewPlanForm({ ...newPlanForm, boardHeight: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kerf (mm)
                </label>
                <input
                  type="number"
                  step="0.0001"
                  value={newPlanForm.kerfWidth * 1000}
                  onChange={(e) => setNewPlanForm({ ...newPlanForm, kerfWidth: parseFloat(e.target.value) / 1000 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Observações
              </label>
              <textarea
                value={newPlanForm.notes}
                onChange={(e) => setNewPlanForm({ ...newPlanForm, notes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Observações opcionais..."
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={isCreating}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isCreating ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Criando...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    Criar Plano de Corte
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => navigate('/cutting-plans')}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="p-6">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
          <p className="text-amber-800">Plano de corte não encontrado</p>
          <button
            onClick={() => navigate('/cutting-plans')}
            className="text-amber-600 hover:text-amber-800 text-sm mt-2"
          >
            Voltar para lista de planos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <nav className="flex items-center gap-2 text-sm text-gray-600 mb-4">
        <button
          onClick={() => navigate('/cutting-plans')}
          className="flex items-center gap-1 hover:text-gray-900"
        >
          <Home className="w-4 h-4" />
          Planos de Corte
        </button>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-900 font-medium">{plan.plan_code}</span>
      </nav>

      <CuttingPlanHeader
        plan={plan}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <div className="flex items-center justify-between mt-6 mb-4">
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('view')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
              activeTab === 'view'
                ? 'bg-white shadow text-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Eye className="w-4 h-4" />
            Visualização
          </button>
          <button
            onClick={() => setActiveTab('boards')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
              activeTab === 'boards'
                ? 'bg-white shadow text-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Grid3x3 className="w-4 h-4" />
            Chapas ({boards.length})
          </button>
        </div>

        {activeTab === 'view' && (
          <div className="flex gap-2">
            {boards.length > 0 && (
              <button
                onClick={() => navigate(`/cutting-plans/${id}/result`)}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              >
                <Eye className="w-4 h-4" />
                Ver Resultado
              </button>
            )}
            <button
              onClick={handleViewModules}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Gerenciar Módulos
            </button>
          </div>
        )}

        {activeTab === 'boards' && (
          <button
            onClick={handleCreateBoard}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nova Chapa
          </button>
        )}
      </div>

      <div className="space-y-6">
        {activeTab === 'view' && (
          <>
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              <div className="xl:col-span-2">
                <CuttingPlanSummary
                  plan={plan}
                  boards={boards}
                  positions={positions}
                />
              </div>
              <div>
                <CuttingPlanTimeline plan={plan} />
              </div>
            </div>

            <CuttingPlanPartsList
              parts={parts}
              positions={positions}
              loading={loading}
            />

            <CuttingPlanBoardsPreview
              boards={boards}
              positions={positions}
              loading={loading}
            />
          </>
        )}

        {activeTab === 'boards' && (
          <>
            {boards.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <Grid3x3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma chapa encontrada</h3>
                <p className="text-gray-500 mb-4">Comece adicionando chapas a este plano.</p>
                <button
                  onClick={handleCreateBoard}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  Adicionar Chapa
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {boards.map((board) => (
                  <BoardCard
                    key={board.id}
                    board={board}
                    onEdit={handleEditBoard}
                    onDelete={handleDeleteBoard}
                    onViewCut={handleViewBoardCut}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <BoardFormModal
        board={editingBoard}
        cutPlanId={id}
        isOpen={showBoardForm}
        onClose={() => setShowBoardForm(false)}
        onSave={handleSaveBoard}
      />

      {showConfirmModal && (
        <ConfirmModal
          message={confirmMessage}
          onConfirm={handleConfirm}
          onCancel={handleCancelConfirm}
        />
      )}
    </div>
  );
};

export default CuttingPlanDetail;
